import { createSelector } from 'reselect';
import { getMACRS, getCashITC, getITC } from '../tax/selectors';
import { getSRECs } from '../srecs/selectors';
import { getNYRebateTotal, getNYPTaxAbatement, getNYResiTaxCredit } from '../nyserda/selectors';
import { getSMARTvalue, getMAStateTaxCredit } from '../smart/selectors';
import { selectSGIPIncentiveValue } from '../../common/sgip/selectors';

const formattedSystemProduction = (state) => {
    const { horizonLength } = state.settings;
    const { systemSize, annualDeg, productionRatio } = state.solar;

    const results = {};

    for (let year = 1; year <= horizonLength; year += 1) {
        const degredation = 1 - (annualDeg / 100 * (year - 1));
        const value = systemSize * productionRatio * degredation;
        results[year] = value;
    }

    return results;
};

export const getSystemProductionForYear = (year, state) => {
    const { systemSize, annualDeg, productionRatio } = state.solar;
    const degredation = 1 - ((annualDeg / 100 * (year - 1)));
    let value = null;
    if (year === 1) {
        value = systemSize * (productionRatio / 1000);
    } else {
        value = systemSize * (productionRatio / 1000) * degredation;
    }

    return value;
};

export const getFormattedSystemProduction = createSelector(formattedSystemProduction, prod => prod);

export const annualSolarValueForYear = (year, prevSolarValue, state) => {
    const { solarValue } = state.solar;
    const { annualInflation } = state.utility;

    if (year === 1) return solarValue;

    return prevSolarValue * (1 + annualInflation / 100);
};

export const systemProduction = (state) => {
    const { horizonLength } = state.settings;
    const { systemSize, annualDeg, productionRatio } = state.solar;

    const results = {};

    for (let year = 1; year <= horizonLength; year += 1) {
        const degredation = 1 - ((annualDeg / 100 * (year - 1)));
        const value = systemSize * (productionRatio / 1000) * degredation;
        results[year] = value;
    }
    return results;
};

export const formattedAnnualSolarSavings = (year, prevSolarValue, state) => {
    const savings =
        annualSolarValueForYear(year, prevSolarValue, state) * systemProduction(state)[year];
    return savings;
};

export const annualSolarSavings = (year, prevSolarValue, state) => {
    const savings =
        annualSolarValueForYear(year, prevSolarValue, state) * systemProduction(state)[year];
    return savings;
};
export const formattedSystemPrice = (state) => {
    const { cashprice } = state.solar;

    const price = cashprice * 1;
    return price;
};

export const formattedSystemPriceESS = (state) => {
    const { cashprice } = state.solar;
    const { includeESS, essPrice } = state.ess;

    let storagePrice = 0;
    if (includeESS === true) {
        storagePrice = essPrice;
    } else {
        storagePrice = 0;
    }
    const price = cashprice * 1 + storagePrice * 1;
    return price;
};

export const annualCashSavings = (year, prevSolarValue, state) => {
    const { cashprice } = state.solar;
    const { projectState } = state.projectaddress;
    const { sgipToggle } = state.sgip;

    const srecs = getSRECs(state);
    const smart = getSMARTvalue(state);
    const maCredit = getMAStateTaxCredit(state, year);
    let sgip = 0;
    if (projectState === 1 && sgipToggle === true) {
        sgip = selectSGIPIncentiveValue(state);
    } else {
        sgip = 0;
    }
    let nyserdaRebate = 0;
    if (projectState === 4) {
        nyserdaRebate = getNYRebateTotal(state);
    } else {
        nyserdaRebate = 0;
    }
    const nyResiTaxCredit = getNYResiTaxCredit(state, year);

    const nyAbatement = getNYPTaxAbatement(state, year) / 4;

    const annualSavings = annualSolarSavings(year, prevSolarValue, state);

    let savings = 0;
    if (year === 1 && projectState === 1 && sgipToggle === true) {
        savings =
            annualSavings +
            getMACRS(state, year) +
            getCashITC(state) +
            sgip +
            (sgip / 5);
    } else if (year === 1 && projectState === 1) {
        savings =
            annualSavings +
            getMACRS(state, year) +
            getCashITC(state) -
            cashprice;
    } else if (year < 6 && projectState === 1 && sgipToggle === true) {
        savings =
            annualSavings +
            (sgip / 5);
    } else if (year === 1 && projectState === 2) {
        savings =
            annualSavings +
            getMACRS(state, year) +
            getCashITC(state) +
            smart[year] +
            maCredit -
            cashprice;
    } else if (year === 1 && projectState === 2) {
        savings =
            annualSavings +
            getMACRS(state, year) +
            getCashITC(state) +
            smart[year] +
            maCredit -
            cashprice;
    } else if (year > 1 && projectState === 2) {
        savings =
            annualSavings +
            smart[year];
    } else if (year === 1 && projectState === 3) {
        savings =
            annualSavings +
            getMACRS(state, year) +
            getCashITC(state) +
            srecs[year] -
            cashprice;
    } else if (year === 1 && projectState === 3) {
        savings =
            annualSavings +
            getMACRS(state, year) +
            getCashITC(state) +
            srecs[year] -
            cashprice;
    } else if (year === 1 && projectState === 4) {
        savings = annualSavings +
            getMACRS(state, year) +
            nyserdaRebate +
            nyResiTaxCredit +
            nyAbatement +
            getCashITC(state) -
            cashprice;
    } else if (year === 1 && projectState === 4) {
        savings = annualSavings +
            getMACRS(state, year) +
            nyserdaRebate +
            nyResiTaxCredit +
            nyAbatement +
            getCashITC(state) -
            cashprice;
    } else if (year > 1 && projectState === 4) {
        savings = annualSavings +
            nyAbatement;
    } else if (year === 1) {
        savings = annualSavings +
            getMACRS(state, year) +
            getCashITC(state) -
            cashprice;
    } else {
        savings = annualSavings + srecs[year];
    }


    return savings;
};

export const getYear1CashReturned = (state, year) => {
    const { cashprice } = state.solar;
    const price = cashprice * 1;
    const percentage = year === 1 ? ((price - annualCashSavings) / price) * 100 : null;

    return percentage;
};

export const itcBenefit = (state, year) => {
    const { selectedFinancing } = state.settings;
    const { loanProvider } = state.loan;
    const cashITC = getCashITC(state);
    const mosaicITC = getITC(state);

    let number = 0;
    if (year === 1) {
        if (selectedFinancing === 1) {
            number = 0;
        }
        if (selectedFinancing === 2 && loanProvider === 'mosaic') {
            number = mosaicITC;
        }
        if (selectedFinancing === 2 && loanProvider === 'custom') {
            number = cashITC;
        }
        if (selectedFinancing === 3 || 4 || 5) {
            number = cashITC;
        }
    }
    return number;
};


export const annualIncentives = (state, year) => {
    const { capTerm } = state.cap;

    let incentives = 0;
    const srecs = getSRECs(state);
    const smart = getSMARTvalue(state);
    const maCredit = getMAStateTaxCredit(state, year);
    const nyserdaRebate = getNYRebateTotal(state);
    const nyAbatement = getNYPTaxAbatement(state, year) / 4;
    const nyResiTaxCredit = getNYResiTaxCredit(state, year);


    if (year === 1) {
        incentives =
            srecs[year] +
            smart[year] +
            maCredit +
            nyserdaRebate +
            nyAbatement +
            nyResiTaxCredit;
    } else if (year <= capTerm / 12) {
        incentives =
            smart[year]
            + srecs[year]
            + nyAbatement;
    } else {
        incentives =
            srecs[year] +
            smart[year] +
            nyAbatement;
    }
    return incentives;
};



// WEBPACK FOOTER //
// ./src/containers/common/solar/selectors.js